import { usePopup } from "./PopupProvider";
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, Menu, Settings, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";
import { normalizeFullName, profileInitials } from "../utils/profileName";
import DataRefreshButton from "./DataRefreshButton";

const border = "var(--bq-border)";
const textPrimary = "var(--bq-ink)";
const textMuted = "var(--bq-muted)";
const accent = "#B4454A";

const pageTitles = {
  "/dashboard": "Dashboard", "/input": "Input Questions", "/question-bank": "Question Bank",
  "/history": "History", "/settings": "Settings", "/assessments": "Assessments",
  "/favorites": "Downloads", "/subjects": "Subjects & Topics", "/notifications": "Notifications",
  "/imports": "Import / Export", "/system-status": "System Status",
  "/help": "Help & Documentation", "/admin": "Admin Dashboard", "/admin/dashboard": "Admin Dashboard",
};

const pageDescriptions = {
  "/dashboard": "Overview of your question pool and assessment activity.",
  "/input": "Create questions from your course materials.",
  "/question-bank": "Manage, organize, and prepare your questions.",
  "/history": "Review your recent workspace activity.",
  "/settings": "Manage your workspace and account preferences.",
  "/favorites": "Retrieve files generated from your assessments.",
  "/recycle-bin": "Restore or permanently remove archived content.",
};

const TopBar = ({ onToggleSidebar }) => {
  const location = useLocation();
  const { showConfirm } = usePopup();
  const navigate = useNavigate();
  const profileMenuRef = useRef(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profile, setProfile] = useState(() => ({
    email: localStorage.getItem("email") || "",
    role: localStorage.getItem("role") || "Faculty",
    displayName: normalizeFullName(localStorage.getItem("name")) || "Account",
    program: localStorage.getItem("program_name") || "Program not assigned",
    department: localStorage.getItem("department") || "Department not assigned",
  }));

  const title = pageTitles[location.pathname] || "BloomQuest";
  const description = pageDescriptions[location.pathname] || "Manage your BloomQuest workspace.";
  const initials = profile.displayName === 'Account' ? '?' : profileInitials(profile.displayName);

  useEffect(() => {
    let active = true;
    const syncProfile = () => {
      setProfile({
        email: localStorage.getItem("email") || "",
        role: localStorage.getItem("role") || "Faculty",
        displayName: normalizeFullName(localStorage.getItem("name")) || "Account",
        program: localStorage.getItem("program_name") || "Program not assigned",
        department: localStorage.getItem("department") || "Department not assigned",
      });
    };

    const loadProgram = async () => {
      const role = (localStorage.getItem("role") || "").toLowerCase();
      if (!['faculty', 'student'].includes(role)) return;
      try {
        const response = await fetch(`${API_URL}/user/profile`);
        if (!response.ok) return;
        const data = await response.json();
        if (!active) return;
        const fullName = normalizeFullName(data.full_name);
        localStorage.setItem("name", fullName);
        const program = (Array.isArray(data.programs) ? data.programs : []).find((item) => String(item.id) === String(data.program_id));
        const programName = data.program_name || program?.name || "Program not assigned";
        localStorage.setItem("program_name", data.program_name || program?.name || "");
        localStorage.setItem("department", data.department || "");
        setProfile((current) => ({ ...current, displayName: fullName || "Account", program: programName, department: data.department || "Department not assigned" }));
      } catch {
        // Keep the locally cached profile visible when profile refresh is unavailable.
      }
    };

    syncProfile();
    loadProgram();
    window.addEventListener("profile-updated", syncProfile);
    window.addEventListener("storage", syncProfile);

    return () => {
      active = false;
      window.removeEventListener("profile-updated", syncProfile);
      window.removeEventListener("storage", syncProfile);
    };
  }, []);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) setProfileMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setProfileMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileMenuOpen]);

  const handleLogout = async () => {
    if (!await showConfirm("You will need to sign in again to access your workspace.", "Log out?", { confirmLabel: "Log out" })) return;
    try {
      await fetch(`${API_URL}/logout`, { method: "POST" });
    } catch (error) {
      console.warn("Could not complete the server logout request:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      setProfileMenuOpen(false);
      navigate("/");
    }
  };

  return (
    <div className="bq-user-topbar flex min-h-[76px] shrink-0 items-center justify-between border-b px-6 py-4" style={{ borderColor: border }}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onToggleSidebar?.()} className="rounded-lg p-1.5 transition-colors hover:bg-white" style={{ color: textMuted }} aria-label="Toggle navigation"><Menu size={18} /></button>
        <div>
          <h1 className="text-xl font-semibold" style={{ color: textPrimary }}>{title}</h1>
          <p className="mt-1 text-xs" style={{ color: textMuted }}>{description}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <DataRefreshButton className="rounded-lg p-2 transition-colors hover:bg-white" style={{ color: textMuted }} />
        <div className="relative" ref={profileMenuRef}>
          <button
            type="button"
            className="bq-profile-trigger flex items-center gap-2 rounded-xl p-1.5 text-left transition-colors"
            aria-label="Open profile menu"
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            onClick={() => setProfileMenuOpen((open) => !open)}
          >
            <span className="text-right max-sm:hidden">
              <span className="block text-sm font-medium" style={{ color: textPrimary }}>{profile.displayName}</span>
              <span className="block text-[10px] uppercase tracking-wider" style={{ color: textMuted }}>{profile.email}</span>
              <span className="block text-[10px]" style={{ color: textMuted }}>{profile.program}</span>
              <span className="block text-[10px]" style={{ color: textMuted }}>{profile.department}</span>
            </span>
            <span className="relative flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold" style={{ borderColor: accent, color: accent }}>
              {initials}
              <span className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2">
                <ShieldCheck size={11} />
              </span>
            </span>
            <ChevronDown aria-hidden="true" size={15} />
          </button>
          {profileMenuOpen && (
            <div className="bq-profile-menu absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border p-1.5 shadow-xl" role="menu">
              <div className="border-b px-3 py-2.5 sm:hidden">
                <p className="text-sm font-semibold">{profile.displayName}</p>
                <p className="mt-0.5 truncate text-xs">{profile.email}</p>
              </div>
              <button
                type="button"
                role="menuitem"
                className="bq-profile-menu-item flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm"
                onClick={() => { setProfileMenuOpen(false); navigate("/settings"); }}
              >
                <Settings size={16} aria-hidden="true" />
                Profile settings
              </button>
              <button
                type="button"
                role="menuitem"
                className="bq-profile-menu-item bq-profile-logout flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm"
                onClick={handleLogout}
              >
                <LogOut size={16} aria-hidden="true" />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;
